import { useState } from 'react';
import { Modal, Text, Banner } from '@shopify/polaris';
import { sitesApi, authApi } from '../lib/api';

interface UninstallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function UninstallModal({ isOpen, onClose }: UninstallModalProps) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const handleUninstall = async () => {
    setLoading(true);
    setError('');

    try {
      // Step 1: Get all user's sites
      setStatus('Getting sites...');
      const sitesResponse = await sitesApi.list();
      const sites = sitesResponse.data || [];

      // Step 2: Disconnect Google from each site (like old app)
      if (sites.length > 0) {
        setStatus('Disconnecting Google Analytics...');
        for (const site of sites) {
          try {
            await sitesApi.disconnectGoogle(site.id);
            console.log(`Google disconnected from site: ${site.display_name}`);
          } catch (err) {
            // Continue even if one fails (might not be connected)
            console.log(`Failed to disconnect Google from ${site.display_name}:`, err);
          }
        }
      }

      // Step 3: Delete user account (this also revokes Webflow token)
      setStatus('Deleting account...');
      await authApi.deleteAccount();

      // Step 4: Clear auth token and redirect to login
      localStorage.removeItem('auth_token');

      // Use full page reload to ensure clean state (React Router navigate had issues)
      window.location.href = '/login?uninstall=success';
    } catch (err) {
      console.error('Uninstall failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to uninstall. Please try again.');
      setLoading(false);
      setStatus('');
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={loading ? () => {} : onClose}
      title="Uninstall Analyzely App"
      primaryAction={{
        content: loading ? status || 'Uninstalling...' : 'Uninstall',
        destructive: true,
        onAction: handleUninstall,
        loading,
        disabled: loading,
      }}
      secondaryActions={
        loading
          ? []
          : [
              {
                content: 'Cancel',
                onAction: onClose,
              },
            ]
      }
    >
      <Modal.Section>
        {error && (
          <div style={{ marginBottom: '16px' }}>
            <Banner tone="critical" onDismiss={() => setError('')}>
              {error}
            </Banner>
          </div>
        )}

        <div style={{ fontSize: '15px', lineHeight: '1.6' }}>
          <Text as="p">
            <span style={{ fontWeight: 500, color: '#bf0711' }}>Warning:</span>{' '}
            Uninstalling the Analyzely app will{' '}
            <span style={{ fontWeight: 500 }}>permanently delete</span> all personal
            and site data associated with this app. This action cannot be{' '}
            <span style={{ fontWeight: 500 }}>undone</span>, and you will not be
            able to <span style={{ fontWeight: 500 }}>recover</span> any of the
            deleted data.
          </Text>

          <div style={{ marginTop: '16px' }}>
            <Text as="p" tone="subdued">
              This will:
            </Text>
            <ul style={{ margin: '8px 0 0 20px', color: '#6d7175' }}>
              <li>Disconnect Google Analytics from all your sites</li>
              <li>Remove tracking code from your Webflow sites</li>
              <li>Delete all analytics configurations</li>
              <li>Revoke Webflow access token</li>
              <li>Delete your account and all associated data</li>
            </ul>
          </div>

          <div style={{ marginTop: '20px' }}>
            <Text as="p">
              <span style={{ fontWeight: 600 }}>Remember</span> that you can
              always reinstall the app to enjoy its features again.
            </Text>
          </div>
        </div>
      </Modal.Section>
    </Modal>
  );
}
